export interface EmploymentTitle {
    title: string;
    titleDate: string;
    summaryPoints: string[];
}

export interface EmploymentData {
    companyName: string;
    location: string;
    employmentDate: string;
    description: string;
    employmentTitles: EmploymentTitle[];
}

const data: EmploymentData[] = [
    {
        companyName: 'Tanium',
        location: 'Bellevue, WA',
        employmentDate: 'April 2023 - September 2026',
        description:
            'Tanium empowers users to query complete and accurate data about their workstations, servers, and other endpoints at scale in real time. As a senior software engineer, my responsibilities included the maintenance, feature work, and refinement of backend services across the Tanium ecosystem.',
        employmentTitles: [
            {
                title: 'Senior Software Engineer',
                titleDate: 'April 2023 - September 2026',
                summaryPoints: [
                    'Architected and implemented backend services for the external attack surface management module from concept to production, collaborating with the graph database team to integrate data sources.',
                    'Designed extensible report generation framework supporting multiple report types and risk assessment exports, working with Security to ensure metrics met compliance requirements.',
                    'Designed industry designation resolution for Benchmark with graceful fallback from customer configuration, through Tanium-managed settings, to the license, plus a Postgres-backed change log with cache invalidation to trigger reprocessing of peer-benchmark metrics on change.',
                    'Designed and implemented a generic, durable asynchronous job processing pipeline to support Tanium Risk Assessment report generation against a large custom data store.',
                    'Developed test harness for Comply API routes, reducing code duplication and test complexity through a configurable data-driven approach.',
                    'As the recognized Go expert for the Benchmark and External Attack Surface teams, conducted backend code reviews that taught idiomatic Go and codebase structure, caught potential bugs, and simplified implementations into more composable, generic designs.',
                ],
            },
        ],
    },
    {
        companyName: 'Twitter',
        location: 'Seattle, WA',
        employmentDate: 'February 2021 - November 2022',
        description:
            "I worked as a Senior Site Reliability Engineer on Twitter's messaging team. We use Kafka as a backbone for internal communication and processing between internal services. My responsibilities include developing automation to facilitate fleet safety and scalable infrastructure.",
        employmentTitles: [
            {
                title: 'Senior Site Reliability Engineer',
                titleDate: 'February 2021 - November 2022',
                summaryPoints: [
                    'Co-designed and developed a fleet safety service called the Constraint Manager which checks the health and various constraints against on-premises machines and their clusters before beginning maintenance operations.',
                    'Automated the detection and remediation of hosts with various hardware failures within Kafka clusters.',
                    'Wrote internal Apache Airflow DAGs to perform kernel upgrades of on-premises machines and roll the deployment of encryption at rest across the fleet.',
                    'Co-designed and developed the Network Alert Console in a cross team effort which is responsible for identifying and correlating alarms, incident creation, and auto-mitigation of various network device failures.',
                    'Investigated the design for and assisted in the deployment of running Kafka at a large scale on Kubernetes in the Twitter data centers.',
                ],
            },
        ],
    },
    {
        companyName: 'Discovery',
        location: 'Bellevue, WA',
        employmentDate: 'September 2019 - January 2021',
        description:
            "I worked as a Senior Software Development Engineer at Discovery's Bellevue office. I have worked on various systems involving ingesting different video sources and metadata that is propagated to various regions around the world. The last team I was on was the transcoding team that converts raw video files into different packaging formats such as HLS and DASH.",
        employmentTitles: [
            {
                title: 'Senior Software Development Engineer',
                titleDate: 'September 2019 - January 2021',
                summaryPoints: [
                    'Worked on the implementation of our transcoding workflow using the Uber Cadence workflow engine.',
                    'Frequently mentored coworkers on the Go language and the best practices.',
                    'Assisted in the design and implementation of various serverless components such as AWS Lambdas to validate, store, and replicate video metadata.',
                ],
            },
        ],
    },
    {
        companyName: 'iStreamPlanet',
        location: 'Seattle, WA',
        employmentDate: 'January 2016 - September 2019',
        description:
            "iStreamPlanet offers the backend streaming solution that has been used for well-known live events such as the Olympics, BlizzCon, and March Madness. I have been involved with many teams of talented individuals usually centered around the company's flagship product and transcoding pipeline, Aventus. In addition, I was involved in the overall design and implementation of Orbis, a direct-to-consumer platform for user, billing, and content management from the initial concept to its full release.",
        employmentTitles: [
            {
                title: 'Senior Software Engineer',
                titleDate: 'January 2016 - September 2019',
                summaryPoints: [
                    'Automated NBA schedule aggregation, storage, and Aventus channel management by integrating various cloud services such as AWS RDS and Kafka.',
                    'Security focused development to stay within PCI and PII compliance for our commerce and user management platform.',
                    'One of the key players in the design, architecture, and implementation of the Orbis platform from initial concept to release.',
                    'Spearheaded the implementation for Microsoft PlayReady into the Atlas DRM product.',
                    'Increased the overall transactions per second capability by the DRM service machines by offloading the responsibilities of processing logs to a separate instance with a custom solution written in Go.',
                    'Responsible for increasing the rate of detection and reaction of problematic ingest and output streams allowing for faster response times.',
                ],
            },
        ],
    },
    {
        companyName: 'Predixion Software',
        location: 'Redmond, WA',
        employmentDate: 'June 2011 - November 2015',
        description:
            'Predixion was a small startup company where I worked with a highly intelligent and motivated team developing the latest in predictive analytics technology. Our product empowered even the most novice of users to see the full benefits of their data. Joining in the summer of 2011 as an intern focusing on quality assurance, I continued with the company after my internship to become a Software Development Engineer in Test. Later I switched my focus towards development and changed my title to Software Development Engineer after making a suite of tools and features. In both roles my responsibilities included working on the Predixion Insight Server and the Predixion Solutions Accelerator.',
        employmentTitles: [
            {
                title: 'Software Development Engineer',
                titleDate: 'June 2014 - November 2015',
                summaryPoints: [
                    'Expanded Predixion Insight application by adding features to the proprietary Excel-like Predictive Analytic Expression language.',
                    'Took over feature development on Predixion Insight setup projects, using various technologies such as PowerShell, Visual Studio Deployment, and Inno Setup.',
                    'Maintained the Predixion Insight custom SQL Server Integration Services (SSIS) components, empowering users to benefit from predictive queries and transformations in the SSIS environment.',
                    'Created Windows Presentation Foundation and Windows Forms controls for modeling and data extraction wizards.',
                ],
            },
            {
                title: 'Software Development Engineer in Test',
                titleDate: 'June 2013 - June 2014',
                summaryPoints: [
                    'Developed a command line tool for the Predixion Solutions Accelerator application, a product that allows users to rapidly prototype a predictive solution for their data. The tool itself eased the process in setting up the database and refreshing the data without having the user interact with stored procedures, SQL statements, or scripts themselves.',
                    'Created the framework and process for testing the existing Predixion Insight REST API endpoints.',
                    'Created a configurable web application to report the overall status and test automation of the project.',
                ],
            },
            {
                title: 'Software Development Test Intern',
                titleDate: 'June 2011 - June 2013',
                summaryPoints: [
                    'Created a PowerShell script to automatically connect, install, and snapshot different base states of Hyper-V Virtual Machines using built-in Windows Management Instrumentation.',
                    'Worked in collaboration with others to create an automated testing framework and maintained various tools to assist in the software development lifecycle.',
                ],
            },
        ],
    },
];

export default data;
